import React, { useState, useEffect, useRef } from 'react';
import { getApiEndpoint } from 'utils/centralizedConfig';
import { buildSdkUrl } from 'utils/sdkUrlHelper';
import StylingMockLayout from '../shared/StylingMockLayout';
import MarketCard from 'components/ui/MarketCard';
import InfoBlock from 'components/ui/InfoBlock';
import ProductToggle from 'components/ui/ProductToggle/ProductToggle';
import InteractiveNav from 'components/ui/InteractiveNav/InteractiveNav';
import DataTable from 'components/ui/DataTable/DataTable';
import { Checkbox } from 'feSharedIndex';
import FormBuilder from 'components/form/FormBuilder/FormBuilder';
import Modal from 'components/ui/Modal/Modal';
// Chat components removed - using Bot SDK as single source of truth
import SlideOver from 'components/ui/SlideOver';
import TabNav from 'components/ui/TabNav/TabNav';
import Badge from 'components/ui/Badge/Badge';
import Alert from 'components/ui/Alert/Alert';
import Pagination from 'components/ui/Pagination/Pagination';
import BesperBotWidget from 'components/BesperBotWidget';
import { OutlineButton } from 'components/button';
import './StylingMockUI.css';

/**
 * Styling Mock UI Elements Page
 * Showcases UI components with interactive controls
 */
const StylingMockUI = () => {
  
  // MarketCard state
  const [marketCardTitle, setMarketCardTitle] = useState('Market Card');
  const [marketCardDescription, setMarketCardDescription] = useState('This is a market card component');
  const [marketCardFeatures, setMarketCardFeatures] = useState('Feature 1, Feature 2, Feature 3');
  const [marketCardBadge, setMarketCardBadge] = useState('');
  
  // InfoBlock state
  const [infoBlockNumber, setInfoBlockNumber] = useState('01');
  const [infoBlockTitle, setInfoBlockTitle] = useState('Info Block');
  const [infoBlockText, setInfoBlockText] = useState('This is an info block component with number, title, and text');
  
  // ProductToggle state
  const [activeTab, setActiveTab] = useState('customer-self-service');
  const [toggleLabel1, setToggleLabel1] = useState('Option 1');
  const [toggleLabel2, setToggleLabel2] = useState('Option 2');

  // InteractiveNav state
  const [interactiveNavActiveItem, setInteractiveNavActiveItem] = useState('dual-mode');

  // TabNav state
  const [tabNavActiveTab, setTabNavActiveTab] = useState('tenants');

  // Form inputs state
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // SlideOver state
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
  const [slideOverClosing, setSlideOverClosing] = useState(false);
  const [slideOverActiveTab, setSlideOverActiveTab] = useState('overview');

  // SDK container ID for styling mock demo
  const SDK_CONTAINER_ID = 'styling-mock-sdk-container';
  const sdkInstanceRef = useRef(null);

  // Helper to parse features from comma-separated string
  const parseFeatures = (featuresString) => {
    return featuresString.split(',').map(f => f.trim()).filter(f => f.length > 0);
  };

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize SDK for styling mock demo
  useEffect(() => {
    const initializeSDK = async () => {
      try {
        const baseUrl = await getApiEndpoint('public');
        const container = document.getElementById(SDK_CONTAINER_ID);
        if (!container) return;

        const botId = 'public_bot_external'; // Use public bot for demo
        const scriptUrl = buildSdkUrl(baseUrl, 'bots', botId, 'container');
        
        const existingScript = document.querySelector(`script[src*="/api/v1/sdk/bots/${botId}"]`);
        if (existingScript && customElements.get('besper-bot-widget')) {
          const existingWidget = container.querySelector('besper-bot-widget');
          if (existingWidget) return;
          
          const widgetElement = document.createElement('besper-bot-widget');
          widgetElement.setAttribute('bot-id', botId);
          widgetElement.setAttribute('api-endpoint', baseUrl);
          widgetElement.setAttribute('mode', 'inline');
          container.appendChild(widgetElement);
          return;
        }

        const script = document.createElement('script');
        script.src = scriptUrl;
        script.type = 'module';
        script.async = true;
        script.setAttribute('data-container', SDK_CONTAINER_ID);
        document.head.appendChild(script);

        sdkInstanceRef.current = { destroy: () => {} };
      } catch (error) {
        console.error('Failed to initialize SDK for styling mock:', error);
      }
    };

    initializeSDK();

    return () => {
      if (sdkInstanceRef.current && sdkInstanceRef.current.destroy) {
        sdkInstanceRef.current.destroy();
      }
      const container = document.getElementById(SDK_CONTAINER_ID);
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
    };
  }, []);

  return (
    <StylingMockLayout>
      <div className="styling-mock-ui">
        <div className="styling-mock-section">
          <h2 className="styling-mock-section-title">UI Elements</h2>
          <p className="styling-mock-section-description">
            Edit the values in real-time to see how components respond. All changes are reflected immediately in the demo.
          </p>
        </div>

        {/* MarketCard */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">MarketCard</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-interactive-controls">
                <div className="styling-mock-control-group">
                  <label>Title:</label>
                  <input
                    type="text"
                    value={marketCardTitle}
                    onChange={(e) => setMarketCardTitle(e.target.value)}
                    className="styling-mock-input"
                  />
                </div>
                <div className="styling-mock-control-group">
                  <label>Description:</label>
                  <textarea
                    value={marketCardDescription}
                    onChange={(e) => setMarketCardDescription(e.target.value)}
                    className="styling-mock-textarea"
                    rows="2"
                  />
                </div>
                <div className="styling-mock-control-group">
                  <label>Features (comma-separated):</label>
                  <input
                    type="text"
                    value={marketCardFeatures}
                    onChange={(e) => setMarketCardFeatures(e.target.value)}
                    className="styling-mock-input"
                    placeholder="Feature 1, Feature 2, Feature 3"
                  />
                </div>
                <div className="styling-mock-control-group">
                  <label>Badge (optional):</label>
                  <input
                    type="text"
                    value={marketCardBadge}
                    onChange={(e) => setMarketCardBadge(e.target.value)}
                    className="styling-mock-input"
                    placeholder="Leave empty for no badge"
                  />
                </div>
              </div>
              <div className="styling-mock-demo-preview">
                <MarketCard
                  title={marketCardTitle}
                  description={marketCardDescription}
                  features={parseFeatures(marketCardFeatures)}
                  badge={marketCardBadge || undefined}
                />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<MarketCard
  title="${marketCardTitle}"
  description="${marketCardDescription}"
  features={[${parseFeatures(marketCardFeatures).map(f => `'${f}'`).join(', ')}]}
  ${marketCardBadge ? `badge="${marketCardBadge}"` : ''}
/>`}
            </div>
          </div>
        </div>

        {/* InfoBlock */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">InfoBlock</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-interactive-controls">
                <div className="styling-mock-control-group">
                  <label>Number:</label>
                  <input
                    type="text"
                    value={infoBlockNumber}
                    onChange={(e) => setInfoBlockNumber(e.target.value)}
                    className="styling-mock-input"
                  />
                </div>
                <div className="styling-mock-control-group">
                  <label>Title:</label>
                  <input
                    type="text"
                    value={infoBlockTitle}
                    onChange={(e) => setInfoBlockTitle(e.target.value)}
                    className="styling-mock-input"
                  />
                </div>
                <div className="styling-mock-control-group">
                  <label>Text:</label>
                  <textarea
                    value={infoBlockText}
                    onChange={(e) => setInfoBlockText(e.target.value)}
                    className="styling-mock-textarea"
                    rows="3"
                  />
                </div>
              </div>
              <div className="styling-mock-demo-preview">
                <InfoBlock
                  number={infoBlockNumber}
                  title={infoBlockTitle}
                  text={infoBlockText}
                />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`<InfoBlock
  number="${infoBlockNumber}"
  title="${infoBlockTitle}"
  text="${infoBlockText.replace(/"/g, '\\"')}"
/>`}
            </div>
          </div>
        </div>

        {/* ProductToggle */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">ProductToggle</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-interactive-controls">
                <div className="styling-mock-control-group">
                  <label>Label 1 (Option 1):</label>
                  <input
                    type="text"
                    value={toggleLabel1}
                    onChange={(e) => setToggleLabel1(e.target.value)}
                    className="styling-mock-input"
                  />
                </div>
                <div className="styling-mock-control-group">
                  <label>Label 2 (Option 2):</label>
                  <input
                    type="text"
                    value={toggleLabel2}
                    onChange={(e) => setToggleLabel2(e.target.value)}
                    className="styling-mock-input"
                  />
                </div>
                <div className="styling-mock-control-group">
                  <label>Active Tab:</label>
                  <select
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value)}
                    className="styling-mock-select"
                  >
                    <option value="customer-self-service">{toggleLabel1}</option>
                    <option value="business-interface">{toggleLabel2}</option>
                  </select>
                </div>
              </div>
              <div className="styling-mock-demo-preview">
                <ProductToggle
                  activeTab={activeTab}
                  onTabChange={(tab) => {
                    console.log('Selected:', tab);
                    setActiveTab(tab);
                  }}
                  labels={{
                    customerSelfService: toggleLabel1,
                    businessInterface: toggleLabel2
                  }}
                />
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Active tab: {activeTab === 'customer-self-service' ? toggleLabel1 : toggleLabel2}
                </p>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`const [activeTab, setActiveTab] = useState('${activeTab}');

<ProductToggle
  activeTab={activeTab}
  onTabChange={(tab) => setActiveTab(tab)}
  labels={{
    customerSelfService: '${toggleLabel1}',
    businessInterface: '${toggleLabel2}'
  }}
/>`}
            </div>
          </div>
        </div>

        {/* InteractiveNav */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">InteractiveNav</h3>
          
          {/* Vertical Orientation */}
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview" style={{ maxWidth: '400px' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Vertical Orientation</h4>
                <InteractiveNav
                  items={[
                    { value: 'dual-mode', label: 'Professional Automation' },
                    { value: 'safe-simple', label: 'Safe & Simple' },
                    { value: 'integrations', label: 'Growing Integrations' },
                    { value: 'enterprise', label: 'Enterprise Ready' }
                  ]}
                  activeItem={interactiveNavActiveItem}
                  onItemClick={(value) => setInteractiveNavActiveItem(value)}
                  autoCycle={true}
                  cycleDuration={10000}
                  orientation="vertical"
                />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`const [interactiveNavActiveItem, setInteractiveNavActiveItem] = useState('dual-mode');

<InteractiveNav
  items={[
    { value: 'dual-mode', label: 'Professional Automation' },
    { value: 'safe-simple', label: 'Safe & Simple' },
    { value: 'integrations', label: 'Growing Integrations' },
    { value: 'enterprise', label: 'Enterprise Ready' }
  ]}
  activeItem={interactiveNavActiveItem}
  onItemClick={(value) => setInteractiveNavActiveItem(value)}
  autoCycle={true}
  cycleDuration={10000}
  orientation="vertical"
/>`}
            </div>
          </div>

          {/* Horizontal Orientation */}
          <div className="styling-mock-showcase" style={{ marginTop: '3rem' }}>
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview" style={{ maxWidth: '100%' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Horizontal Orientation</h4>
                <InteractiveNav
                  items={[
                    { value: 'dual-mode', label: 'Professional Automation' },
                    { value: 'safe-simple', label: 'Safe & Simple' },
                    { value: 'integrations', label: 'Growing Integrations' },
                    { value: 'enterprise', label: 'Enterprise Ready' }
                  ]}
                  activeItem={interactiveNavActiveItem}
                  onItemClick={(value) => setInteractiveNavActiveItem(value)}
                  autoCycle={true}
                  cycleDuration={10000}
                  orientation="horizontal"
                />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`const [interactiveNavActiveItem, setInteractiveNavActiveItem] = useState('dual-mode');

<InteractiveNav
  items={[
    { value: 'dual-mode', label: 'Professional Automation' },
    { value: 'safe-simple', label: 'Safe & Simple' },
    { value: 'integrations', label: 'Growing Integrations' },
    { value: 'enterprise', label: 'Enterprise Ready' }
  ]}
  activeItem={interactiveNavActiveItem}
  onItemClick={(value) => setInteractiveNavActiveItem(value)}
  autoCycle={true}
  cycleDuration={10000}
  orientation="horizontal"
/>`}
            </div>
          </div>
        </div>

        {/* TabNav */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">TabNav</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-interactive-controls">
                <div className="styling-mock-control-group">
                  <label>Active Tab:</label>
                  <select
                    value={tabNavActiveTab}
                    onChange={(e) => setTabNavActiveTab(e.target.value)}
                    className="styling-mock-select"
                  >
                    <option value="tenants">Tenants</option>
                    <option value="users">Users</option>
                    <option value="resources">Resources</option>
                    <option value="costs">Cost Pools</option>
                    <option value="roles">Roles</option>
                  </select>
                </div>
              </div>
              <div className="styling-mock-demo-preview">
                <TabNav
                  tabs={[
                    { id: 'tenants', label: 'Tenants', count: 15 },
                    { id: 'users', label: 'Users', count: 42 },
                    { id: 'resources', label: 'Resources', count: 28 },
                    { id: 'costs', label: 'Cost Pools', count: 7 },
                    { id: 'roles', label: 'Roles', count: 12 }
                  ]}
                  activeTab={tabNavActiveTab}
                  onTabChange={setTabNavActiveTab}
                />
                <div style={{ marginTop: '20px', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
                    Active tab: <strong>{tabNavActiveTab}</strong>
                  </p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    This is the content area for the {tabNavActiveTab} tab. Click on different tabs to see the selection change.
                  </p>
                </div>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`const [tabNavActiveTab, setTabNavActiveTab] = useState('tenants');

<TabNav
  tabs={[
    { id: 'tenants', label: 'Tenants', count: 15 },
    { id: 'users', label: 'Users', count: 42 },
    { id: 'resources', label: 'Resources', count: 28 },
    { id: 'costs', label: 'Cost Pools', count: 7 },
    { id: 'roles', label: 'Roles', count: 12 }
  ]}
  activeTab={tabNavActiveTab}
  onTabChange={setTabNavActiveTab}
/>

{/* Tab content based on activeTab */}
{tabNavActiveTab === 'tenants' && <TenantsContent />}
{tabNavActiveTab === 'users' && <UsersContent />}
{tabNavActiveTab === 'resources' && <ResourcesContent />}
{tabNavActiveTab === 'costs' && <CostPoolsContent />}
{tabNavActiveTab === 'roles' && <RolesContent />}`}
            </div>
          </div>
        </div>

        {/* DataTable */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">DataTable</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <DataTable
                  data={[
                    { id: 1, name: 'John Doe', email: 'john@example.com', status: 'Active' },
                    { id: 2, name: 'Jane Smith', email: 'jane@example.com', status: 'Inactive' },
                    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', status: 'Active' }
                  ]}
                  columns={[
                    { key: 'name', header: 'Name', sortable: true },
                    { key: 'email', header: 'Email', sortable: true },
                    { key: 'status', header: 'Status', sortable: true }
                  ]}
                  searchable={true}
                  sortable={true}
                  paginated={true}
                  defaultPageSize={5}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Builder */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Form Builder</h3>
          <p className="styling-mock-section-description">
            Build forms interactively by adding components, configuring their properties, and previewing the result.
            Export your form configuration as JSON.
          </p>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview" style={{ maxWidth: '100%' }}>
                <FormBuilder />
              </div>
            </div>
          </div>
        </div>

        {/* Badge */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Badge</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <Badge text="Default" variant="default" />
                  <Badge text="Primary" variant="primary" />
                  <Badge text="Secondary" variant="secondary" />
                  <Badge text="Success" variant="success" />
                  <Badge text="Warning" variant="warning" />
                  <Badge text="Danger" variant="danger" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Alert */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Alert</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview" style={{ maxWidth: '600px' }}>
                <Alert variant="info" title="Info" dismissible={false}>
                  This is an informational alert message.
                </Alert>
                <Alert variant="success" title="Success" dismissible={false}>
                  Operation completed successfully!
                </Alert>
                <Alert variant="warning" title="Warning" dismissible={false}>
                  Please review before proceeding.
                </Alert>
                <Alert variant="error" title="Error" dismissible={false}>
                  An error occurred. Please try again.
                </Alert>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Pagination</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <Pagination
                  currentPage={3}
                  totalPages={10}
                  onPageChange={(page) => console.log('Page:', page)}
                  pageSize={10}
                  onPageSizeChange={(size) => console.log('Page size:', size)}
                  showPageSize={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modal */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Modal</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  Click the button below to open the modal
                </p>
                <OutlineButton onClick={() => setIsModalOpen(true)}>
                  Open Modal
                </OutlineButton>
                <Modal
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  title="Modal Example"
                  primaryActionLabel="Save"
                  secondaryActionLabel="Cancel"
                  onPrimaryAction={() => {
                    console.log('Primary action clicked');
                    setIsModalOpen(false);
                  }}
                  onSecondaryAction={() => setIsModalOpen(false)}
                >
                  <p>This is a modal dialog example. You can add any content here.</p>
                </Modal>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`import { OutlineButton } from 'components/button';
import Modal from 'components/ui/Modal/Modal';

const [isModalOpen, setIsModalOpen] = useState(false);

<OutlineButton onClick={() => setIsModalOpen(true)}>
  Open Modal
</OutlineButton>

<Modal
  isOpen={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  title="Modal Example"
  primaryActionLabel="Save"
  secondaryActionLabel="Cancel"
  onPrimaryAction={() => {
    console.log('Primary action clicked');
    setIsModalOpen(false);
  }}
  onSecondaryAction={() => setIsModalOpen(false)}
>
  <p>This is a modal dialog example. You can add any content here.</p>
</Modal>`}
            </div>
          </div>
        </div>

        {/* SlideOver (Sidebar Modal) */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">SlideOver (Sidebar Modal)</h3>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview">
                <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  Click the button below to open the sidebar modal with tabs
                </p>
                <OutlineButton
                  onClick={() => {
                    setIsSlideOverOpen(true);
                    setSlideOverClosing(false);
                  }}
                >
                  Open SlideOver
                </OutlineButton>
                <SlideOver
                  isOpen={isSlideOverOpen}
                  closing={slideOverClosing}
                  onClose={() => {
                    setSlideOverClosing(true);
                    setTimeout(() => {
                      setIsSlideOverOpen(false);
                      setSlideOverClosing(false);
                    }, 220);
                  }}
                  onExited={() => {
                    setIsSlideOverOpen(false);
                    setSlideOverClosing(false);
                  }}
                  width="xl"
                  title="Sidebar Modal with Tabs"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Tabs */}
                    <TabNav
                      tabs={[
                        { id: 'overview', label: 'Overview' },
                        { id: 'details', label: 'Details', count: 5 },
                        { id: 'settings', label: 'Settings' }
                      ]}
                      activeTab={slideOverActiveTab}
                      onTabChange={setSlideOverActiveTab}
                    />

                    {/* Tab Content */}
                    <div style={{ padding: '20px 0' }}>
                      {slideOverActiveTab === 'overview' && (
                        <div>
                          <h4 style={{ marginBottom: '12px', fontSize: '1.1rem', fontWeight: 600 }}>Overview</h4>
                          <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                            This is the overview tab content. The SlideOver component slides in from the right
                            and overlays the page content without moving the layout.
                          </p>
                        </div>
                      )}
                      {slideOverActiveTab === 'details' && (
                        <div>
                          <h4 style={{ marginBottom: '12px', fontSize: '1.1rem', fontWeight: 600 }}>Details</h4>
                          <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                            This is the details tab content. You can add any content here, including forms,
                            tables, or other components.
                          </p>
                          <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <Badge text="Detail 1" variant="primary" />
                            <Badge text="Detail 2" variant="secondary" />
                            <Badge text="Detail 3" variant="success" />
                          </div>
                        </div>
                      )}
                      {slideOverActiveTab === 'settings' && (
                        <div>
                          <h4 style={{ marginBottom: '12px', fontSize: '1.1rem', fontWeight: 600 }}>Settings</h4>
                          <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                            This is the settings tab content. The SlideOver supports different widths:
                            'md' (520px), 'lg' (640px), 'xl' (760px), or 'focus' (80vw).
                          </p>
                          <div style={{ marginTop: '16px' }}>
                            <Checkbox
                              label="Enable notifications"
                              checked={checkboxChecked}
                              onChange={setCheckboxChecked}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </SlideOver>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`import { OutlineButton } from 'components/button';
import SlideOver from 'components/ui/SlideOver';
import TabNav from 'components/ui/TabNav/TabNav';

const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
const [slideOverClosing, setSlideOverClosing] = useState(false);
const [slideOverActiveTab, setSlideOverActiveTab] = useState('overview');

<OutlineButton
  onClick={() => {
    setIsSlideOverOpen(true);
    setSlideOverClosing(false);
  }}
>
  Open SlideOver
</OutlineButton>

<SlideOver
  isOpen={isSlideOverOpen}
  closing={slideOverClosing}
  onClose={() => {
    setSlideOverClosing(true);
    setTimeout(() => {
      setIsSlideOverOpen(false);
      setSlideOverClosing(false);
    }, 220);
  }}
  onExited={() => {
    setIsSlideOverOpen(false);
    setSlideOverClosing(false);
  }}
  width="xl"
  title="Sidebar Modal with Tabs"
>
  <TabNav
    tabs={[
      { id: 'overview', label: 'Overview' },
      { id: 'details', label: 'Details', count: 5 },
      { id: 'settings', label: 'Settings' }
    ]}
    activeTab={slideOverActiveTab}
    onTabChange={setSlideOverActiveTab}
  />
  {/* Tab content here */}
</SlideOver>`}
            </div>
          </div>
        </div>

        {/* Chat Elements */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Chat Elements</h3>
          <p className="styling-mock-section-description">
            Chat components based on the bot SDK. These components can be used to build custom chat interfaces.
          </p>

          {/* Bot SDK - Single Source of Truth */}
          <div className="styling-mock-showcase" style={{ marginTop: '2rem' }}>
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview" style={{ maxWidth: '800px' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Bot SDK Container Mode</h4>
                <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  All bot UI components are provided by the Bot SDK. Use container mode to embed the chat interface directly in your page.
                </p>
                <div 
                  id="styling-mock-sdk-container"
                  style={{ 
                    height: '600px', 
                    border: '1px solid var(--border-color)', 
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                />
              </div>
            </div>
            <div className="styling-mock-code-block">
{`// Bot SDK Container Mode - Single Source of Truth
// All bot UI comes from the SDK, no custom components needed

<div id="besper-bot-container"></div>
<script src="/api/v1/sdk/bots/{bot_id}"></script>

// Or in React:
useEffect(() => {
  const container = document.getElementById('besper-bot-container');
  const script = document.createElement('script');
  script.src = buildSdkUrl(baseUrl, 'bots', botId);
  script.type = 'module';
  script.setAttribute('data-container', 'besper-bot-container');
  document.head.appendChild(script);
}, [botId]);`}
            </div>
          </div>
        </div>

        {/* Bot SDK Container Mode */}
        <div className="styling-mock-section">
          <h3 className="styling-mock-section-title">Bot SDK Container Mode</h3>
          <p className="styling-mock-section-description">
            The bot SDK supports container mode for embedding the chat widget directly into your page layout.
            Unlike widget mode (floating button), container mode renders the chat interface in a designated container element.
          </p>
          <div className="styling-mock-showcase">
            <div className="styling-mock-showcase-content">
              <div className="styling-mock-demo-preview" style={{ maxWidth: '800px' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Container Mode Demo</h4>
                <div 
                  style={{ 
                    height: '600px', 
                    border: '1px solid var(--border-color)', 
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <BesperBotWidget
                    widget={false}
                    containerId="besper-bot-container-demo"
                    resourceId="styling-mock-bot"
                    autoInitialize={true}
                    style={{ height: '100%', width: '100%' }}
                  />
                </div>
              </div>
            </div>
            <div className="styling-mock-code-block">
{`// Container Mode - Embed chat widget in your page layout
import BesperBotWidget from 'components/BesperBotWidget';

// Create a container element with a unique ID
<div id="besper-bot-container-demo" style={{ height: '600px' }}>
  <BesperBotWidget
    widget={false}
    containerId="besper-bot-container-demo"
    autoInitialize={true}
  />
</div>

// Key differences from widget mode:
// - widget: false (required for container mode)
// - containerId: unique ID of the container element
// - The container element must exist in DOM before SDK initialization
// - Chat interface is embedded in your page layout, not floating`}
            </div>
          </div>
        </div>
      </div>
    </StylingMockLayout>
  );
};

export default StylingMockUI;

